// Notifications, bottom right. An agent's update arrives here with its name
// on it; clicking one takes you where it happened: its board, and the page,
// step, post, deal, task or widget it is about (Features 1, 20 Oct).

import { Check, ChevronRight, CircleAlert, Info, TriangleAlert, X } from "lucide-react";
import { agentName } from "../../shared/authors";
import { cx } from "../lib/util";
import { Linked } from "../lib/Linked";
import { dismissToast, get, set, useStore } from "../state/store";
import { camera } from "../canvas/camera";
import { bounds, itemRect } from "../canvas/geometry";
import { switchBoard } from "../state/actions";
import { openPlace, placeName } from "../state/place";
import { Who } from "./agentMarks";

export function Toasts() {
  const toasts = useStore((s) => s.toasts);
  return (
    <div className="toasts" aria-live="polite">
      {toasts.map((t) => {
        const Icon = t.level === "success" ? Check : t.level === "warn" ? TriangleAlert : t.level === "error" ? CircleAlert : Info;
        const isAgent = t.who && t.who !== "You" && t.who !== "Context Board";
        // the widgets it is about: one, or every one a run of changes touched
        const ids = (t.itemIds?.length ? t.itemIds : t.itemId ? [t.itemId] : []).filter((id) => get().items[id]);
        const its = ids.map((id) => get().items[id]!);
        // a run of changes to widgets on one board shows them all; anything else goes where it says
        const several = its.length > 1 && its.every((i) => i.boardId === its[0].boardId);
        const to = t.to ?? null;
        const where = several ? (get().boards.find((b) => b.id === its[0].boardId)?.name ?? null) : to ? placeName(to) : its[0] ? (get().boards.find((b) => b.id === its[0].boardId)?.name ?? null) : null;
        const goes = several || !!to || its.length > 0;
        const go = () => {
          if (!goes) return;
          dismissToast(t.id);
          if (!several && to) {
            openPlace(to);
            return;
          }
          if (its[0].boardId !== get().boardId) switchBoard(its[0].boardId);
          const here = its.filter((i) => i.boardId === its[0].boardId);
          set({ selection: here.map((i) => i.id) });
          const box = bounds(here.map(itemRect));
          if (box) camera.fit(box, { maxZ: 1, pad: 140 });
        };
        // where a click goes, quiet until the pointer is on it: beside the agent's name, or under the words
        const open = (
          <button
            className="toast-open"
            onClick={(e) => {
              e.stopPropagation();
              go();
            }}
          >
            <span>{where ? `Open ${where}` : "Open"}</span>
            <ChevronRight size={12} aria-hidden />
          </button>
        );
        return (
          <div key={t.id} className={cx("toast island", goes && "has-item")} onClick={go} role="status">
            {isAgent ? (
              <Who name={agentName(t.who!)} via={t.via} />
            ) : (
              <span className={cx("toast-ico", `lvl-${t.level}`)}>
                <Icon size={14} />
              </span>
            )}
            <div className="toast-main">
              {isAgent && (
                <div className="toast-who">
                  <span>{agentName(t.who)}</span>
                  {goes && !t.action && open}
                </div>
              )}
              <div className="toast-title">
                <Linked text={t.title} />
              </div>
              {t.body && (
                <div className="toast-body">
                  <Linked text={t.body} />
                </div>
              )}
              {(t.action || (goes && !isAgent)) && (
                <div className="toast-action">
                  {t.action && (
                    <button
                      className="btn btn-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        t.action!.run();
                        dismissToast(t.id);
                      }}
                    >
                      {t.action.label}
                    </button>
                  )}
                  {goes && (t.action || !isAgent) && open}
                </div>
              )}
            </div>
            <button
              className="icon-btn sm toast-close"
              aria-label="Dismiss"
              onClick={(e) => {
                e.stopPropagation();
                dismissToast(t.id);
              }}
            >
              <X size={13} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
